import PropTypes from 'prop-types';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import React from 'react';
import { globalStyles, gstheme } from '../style/gstheme';

const styles = theme => {
  return Object.assign({
    importOverLay: {
      zIndex: 30,
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      backgroundColor: '#e0e0e0',
      opacity: 0.2
    },
    errorReport: {
      zIndex: 40,
      marginBottom: '30px',
      border: 'solid 1px #DD2C00',
      borderRadius: '4px'
    },
    warningReport: {
      zIndex: 40,
      marginBottom: '30px',
      border: 'solid 1px #CA4F0C',
      borderRadius: '4px'
    },
    errorMessages: {
      display: 'block',
    },
    mainError: {
      fontWeight: 500
    },
    errorGroup: {
      marginBottom: '24px'
    },
    errorDetail: {
      fontSize: '13px'
    },
    linePart: {
      display: 'block'
    },
    importOuter: {
      backgroundColor: theme.palette.grey[100],
      marginBottom: '10px',
      borderRadius: '4px',
      textAlign: 'center',
      padding: '18px',
      height: '250px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    importInner: {
      border: 'solid 1px ' + theme.palette.grey[500],
      backgroundColor: theme.palette.grey[100],
      borderRadius: '4px',
      textAlign: 'center',
      paddingTop: 30,
      height: '200px',
      paddingBottom: 20
    },
    progress: {
      width: '262px',
      height: '6px',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: '30px'
    }
  }, globalStyles);
};

const minorIssueText = [
  "Data contains an empty ontology. This means that any classes or properties are using a different namespace to that used by the ontology definition. Ignoring this issue may load the ontology but you will not see any classes or properties listed.",
  "Data contains literals on object properties:",
  "Data contains properties with anyURI values where an IRI expected.",
  "Data contains properties with unsupported literal values. Only strings and language strings are permitted on SKOS, DC and RDFS properties.",
  "Data contains IRIs on properties where only literals supported.",
  "Named individuals should have a type.",
  "Named individual has invalid properties.",
  "Data contains equivalent properties of differing types.",
  "Cardinalities should be non-negative integers.",
  "A restriction should only contain one of a set of properties.",
  "Data contains equivalent property statements for inapplicable resources.",
  "Data contains equivalent property statements to properties that are annotation properties.",
  "Data contains inverse functional properties on invalid resources.",
  "Data contains different from statements to things that other than named individuals.",
  "Data contains same as statements for things other than named individuals.",
  "Data contains same as statements to things that other than named individuals.",
  "Data contains different from statements for things other than named individuals."
];

function Upload(props) {

  const { transfer, message, classes, normalisation, filename, allowMinorIssues, issueManagement } = props;
  const messages = message ? (Array.isArray(message.en) ? message.en : [message.en]) : null;
  let progressValue = 0;
  if (transfer === true) progressValue++;
  if (normalisation !== null) progressValue += normalisation.length;
  let status = 'Uploading';
  if (props.status) {
    if (props.stepCount) status = 'Processing';
    else if (props.status.includes("https://grafsync.graphifi.com/schema/gs/pending")) status = 'Loading Pending';
    else if (props.status.includes("https://grafsync.graphifi.com/schema/gs/uploaded")) status = 'Running Checks';
    else if (props.status.includes("https://grafsync.graphifi.com/schema/gs/initialising")) status = 'Loading for Processing';
  }
  let done = [];
  if (props.status) {
    if (props.stepCount) done.push("All Checks Completed");
    if (props.status.includes("https://grafsync.graphifi.com/schema/gs/projectTypeChecksValid")) done.push("Project Specific Validation Completed");
    if (props.status.includes("https://grafsync.graphifi.com/schema/gs/validIRIs")) done.push("IRI Check Completed");
    if (props.status.includes("https://grafsync.graphifi.com/schema/gs/validated")) done.push("Data Validated");
    if (props.status.includes("https://grafsync.graphifi.com/schema/gs/uploaded")) done.push("Loading for Processing Completed");
  }

  return (
    <React.Fragment>
      {transfer !== false && <div data-test="overlay" className={classes.importOverLay} />}
      <div className={classes.importOuter}>
        <div className={classes.importInner}>
          {transfer === false &&
            <Typography align="center">
              <ErrorIcon style={{ height: '50px', width: '50px', color: '#DD2C00' }} />
            </Typography>
          }
          {transfer === true &&
            <Typography align="center">
              <CheckCircleIcon color="secondary" style={{ height: '50px', width: '50px' }} />
            </Typography>
          }
          <Typography data-test="upload-info" paragraph className={classes.mainHeading}
            style={{
              marginTop: '0px', color: transfer === false ? '#DD2C00' : (
                transfer !== true ? gstheme.palette.primary.main : gstheme.palette.secondary.main
              )
            }}
            align="center">
            {transfer !== null ?
              (transfer === false ? 'Upload Failed' : 'Upload Successful')
              :
              status
            }
          </Typography>
          {transfer === null && props.stepCount &&
            <LinearProgress color="secondary" className={classes.progress} variant="determinate" value={(progressValue / (props.stepCount + 1)) * 100} />
          }
          {transfer === null && done.map((isDone, index) =>
            <Typography key={index} align="center" variant="body2" style={{ color: '#212121' }}>{isDone}</Typography>)}
        </div>
      </div>
      {
        transfer !== null && messages &&
        <Accordion data-test="error-details" className={allowMinorIssues ? classes.warningReport : classes.errorReport}>
          <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
            {allowMinorIssues && transfer === true ?
              <Typography data-test="ignored" className={classes.subHeading}>
                <span style={{ color: '#CA4F0C' }}>
                  <WarningIcon style={{ verticalAlign: 'middle' }} /> Ignored Error Details and Import Notes ({filename})
                </span>
              </Typography>
              :
              <Typography data-test="errored" className={classes.subHeading} style={{ color: '#DD2C00' }}>
                <span style={{ color: '#DD2C00' }}>
                  <ErrorIcon style={{ transform: 'translateY(6px)' }} /> Error Details ({filename})
                </span>
              </Typography>
            }
          </AccordionSummary>
          <AccordionDetails className={classes.errorMessages}>
            <div data-test="import-errors">
              {[...messages].sort().map((reason, index) => {
                const parsedReason = reason.split("\\n");
                return <div
                  data-test={"error-" + index}
                  key={index}
                  className={classes.errorGroup}>
                  {
                    parsedReason.map((reasonPart, partIndex) =>
                      <div
                        data-test={"error-" + index + "-" + partIndex + "-container"}
                        style={{overflowWrap: 'break-word' }}
                        key={index + "-" + partIndex}>
                        <Typography
                          className={partIndex === 0 ? classes.mainError : classes.errorDetail}
                          data-test={"error-" + index + "-" + partIndex}
                          paragraph={!issueManagement || allowMinorIssues || !minorIssueText.includes(reasonPart)}
                          variant="body2">
                          {reasonPart.split("\\t").map((linePart, linePartIndex) => <span className={classes.linePart} key={linePartIndex}>{linePart}</span>)}
                        </Typography>
                        {issueManagement && !allowMinorIssues && minorIssueText.includes(reasonPart) &&
                          <Typography data-test="fixable" style={{ color: '#CA4F0C' }} paragraph variant="caption">Minor error (fixable by selecting Ignore Minor Errors)</Typography>
                        }
                      </div>
                    )}
                </div>;
              })}
            </div>
          </AccordionDetails>
        </Accordion>
      }
    </React.Fragment>
  );

}

Upload.defaultProps = {
  ignoreMinorIssues: false,
  issueManagement: false
};

Upload.propTypes = {
  disableScreen: PropTypes.bool,
  filename: PropTypes.string,
  normalisation: PropTypes.array,
  issueManagement: PropTypes.bool,
  stepCount: PropTypes.number,
  status: PropTypes.array,
  message: PropTypes.object,
  transfer: PropTypes.bool,
  classes: PropTypes.object
};

export default withStyles(styles)(Upload);